import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Sparkles, Check, Star, Zap, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { analytics } from '../analytics';

const SWIPE_THRESHOLD = 100;
const MAX_ROTATION = 12;
const FLY_OUT_DURATION = 300;

/**
 * TinderSwipeResults — one-at-a-time swipe UI for gift recommendations.
 * Receives the same props as the grid view for drop-in replacement.
 */
export default function TinderSwipeResults({
  gifts,
  onBuyClick,
  currency,
  getCurrencySymbol,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [decisions, setDecisions] = useState([]); // { gift, liked: bool }[]
  const [dragState, setDragState] = useState({ x: 0, y: 0, dragging: false });
  const [flyOut, setFlyOut] = useState(null); // 'left' | 'right' | null
  const [expanded, setExpanded] = useState(false);
  const [lastUndo, setLastUndo] = useState(null); // index that was undone (for 1-level undo)
  const [done, setDone] = useState(false);

  const cardRef = useRef(null);
  const startPos = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);

  const filteredGifts = gifts;
  const currentGift = filteredGifts[currentIndex];
  const totalGifts = filteredGifts.length;

  // Track gift view when card changes
  useEffect(() => {
    if (currentGift && !done) {
      analytics.giftViewed(currentIndex, currentGift.title, false);
    }
  }, [currentIndex, currentGift, done]);

  // --- Swipe logic ---

  const animateOut = useCallback((direction) => {
    setFlyOut(direction);
    setTimeout(() => {
      setFlyOut(null);
      setDragState({ x: 0, y: 0, dragging: false });

      const gift = filteredGifts[currentIndex];
      const liked = direction === 'right';

      // Track swipe event
      if (liked) {
        analytics.swipeRight(gift.title, currentIndex + 1, gift.storeName);
      } else {
        analytics.swipeLeft(gift.title, currentIndex + 1, gift.storeName);
      }

      setDecisions(prev => [...prev, { gift, liked }]);
      setLastUndo({ index: currentIndex, direction });
      setExpanded(false);

      // Right swipe = track + open affiliate link (app deep link on mobile)
      if (liked) {
        onBuyClick(gift);
      }

      if (currentIndex + 1 >= totalGifts) {
        setDone(true);
        const likedCount = [...decisions, { liked }].filter(d => d.liked).length;
        analytics.swipeSummaryViewed(likedCount, totalGifts);
      } else {
        setCurrentIndex(prev => prev + 1);
      }
    }, FLY_OUT_DURATION);
  }, [currentIndex, totalGifts, filteredGifts, decisions, onBuyClick]);

  const handleDragStart = useCallback((clientX, clientY) => {
    isDragging.current = true;
    startPos.current = { x: clientX, y: clientY };
    setDragState({ x: 0, y: 0, dragging: true });
  }, []);

  const handleDragMove = useCallback((clientX, clientY) => {
    if (!isDragging.current) return;
    const dx = clientX - startPos.current.x;
    const dy = clientY - startPos.current.y;
    setDragState({ x: dx, y: dy, dragging: true });
  }, []);

  const handleDragEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;

    setDragState(prev => {
      if (prev.x > SWIPE_THRESHOLD) {
        animateOut('right');
      } else if (prev.x < -SWIPE_THRESHOLD) {
        animateOut('left');
      } else {
        return { x: 0, y: 0, dragging: false };
      }
      return prev;
    });
  }, [animateOut]);

  // Touch handlers
  const onTouchStart = useCallback((e) => {
    const t = e.touches[0];
    handleDragStart(t.clientX, t.clientY);
  }, [handleDragStart]);

  const onTouchMove = useCallback((e) => {
    const t = e.touches[0];
    handleDragMove(t.clientX, t.clientY);
  }, [handleDragMove]);

  const onTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Mouse handlers
  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);
  }, [handleDragStart]);

  useEffect(() => {
    const onMouseMove = (e) => handleDragMove(e.clientX, e.clientY);
    const onMouseUp = () => handleDragEnd();

    if (dragState.dragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [dragState.dragging, handleDragMove, handleDragEnd]);

  // Undo handler (1-level deep)
  const handleUndo = useCallback(() => {
    if (decisions.length === 0) return;

    const lastDecision = decisions[decisions.length - 1];
    analytics.swipeUndo(lastDecision.gift.title, currentIndex);

    setDecisions(prev => prev.slice(0, -1));
    setDone(false);
    setCurrentIndex(prev => prev - 1);
    setLastUndo(null);
    setExpanded(false);
  }, [decisions, currentIndex]);

  // Button swipe actions
  const handleButtonSwipeLeft = useCallback(() => {
    if (done || flyOut) return;
    animateOut('left');
  }, [done, flyOut, animateOut]);

  // --- Card transform ---
  const getCardStyle = (stackIndex) => {
    if (stackIndex === 0) {
      // Active card
      if (flyOut) {
        const x = flyOut === 'right' ? window.innerWidth : -window.innerWidth;
        const rotate = flyOut === 'right' ? MAX_ROTATION : -MAX_ROTATION;
        return {
          transform: `translateX(${x}px) rotate(${rotate}deg)`,
          transition: `transform ${FLY_OUT_DURATION}ms ease-out`,
          zIndex: 30,
        };
      }
      const rotate = (dragState.x / window.innerWidth) * MAX_ROTATION;
      return {
        transform: `translateX(${dragState.x}px) translateY(${dragState.y * 0.3}px) rotate(${rotate}deg)`,
        transition: dragState.dragging ? 'none' : 'transform 0.3s ease-out',
        zIndex: 30,
        cursor: 'grab',
      };
    }
    // Stack cards behind
    const offset = stackIndex * 8;
    const scale = 1 - stackIndex * 0.05;
    return {
      transform: `translateY(${offset}px) scale(${scale})`,
      zIndex: 20 - stackIndex,
      pointerEvents: 'none',
    };
  };

  const swipeOverlayOpacity = Math.min(Math.abs(dragState.x) / SWIPE_THRESHOLD, 1);
  const showLikeOverlay = dragState.x > 20;
  const showSkipOverlay = dragState.x < -20;

  // --- Completion screen ---
  if (done) {
    const likedGifts = decisions.filter(d => d.liked);
    const skippedGifts = decisions.filter(d => !d.liked);

    return (
      <div className="max-w-lg mx-auto mb-8">
        {/* Summary header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full mb-3">
            <Sparkles size={18} className="text-green-600" />
            <span className="font-bold text-green-700">
              You liked {likedGifts.length} out of {totalGifts} gifts!
            </span>
          </div>
          <p className="text-sm text-gray-500">Tap any gift below to buy it</p>
        </div>

        {/* Liked gifts */}
        {likedGifts.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="text-green-500">&#10003;</span> Your Picks
            </h4>
            <div className="space-y-3">
              {likedGifts.map(({ gift }) => (
                <SummaryCard
                  key={gift.id}
                  gift={gift}
                  liked
                  getCurrencySymbol={getCurrencySymbol}
                  onClick={() => {
                    analytics.swipeSummaryClick(gift.title, true);
                    onBuyClick(gift);
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Skipped gifts — second chance */}
        {skippedGifts.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="text-gray-400">&#8212;</span> Skipped — Second Chance?
            </h4>
            <div className="space-y-3">
              {skippedGifts.map(({ gift }) => (
                <SummaryCard
                  key={gift.id}
                  gift={gift}
                  liked={false}
                  getCurrencySymbol={getCurrencySymbol}
                  onClick={() => {
                    analytics.swipeSummaryClick(gift.title, false);
                    onBuyClick(gift);
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Undo last swipe */}
        {decisions.length > 0 && (
          <div className="text-center mt-4">
            <button
              onClick={handleUndo}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <RotateCcw size={14} />
              Go back and re-swipe
            </button>
          </div>
        )}
      </div>
    );
  }

  // --- Main swipe view ---
  return (
    <div className="max-w-lg mx-auto mb-8">
      {/* Progress bar */}
      <div className="flex items-center justify-between mb-4 px-1">
        <span className="text-sm font-semibold text-purple-600">
          Gift {currentIndex + 1} of {totalGifts} — Your AI picks
        </span>
        <div className="flex-1 mx-4 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-300"
            style={{ width: `${((currentIndex + 1) / totalGifts) * 100}%` }}
          />
        </div>
      </div>

      {/* Card stack */}
      <div className="relative" style={{ height: 520, touchAction: 'pan-y' }}>
        {filteredGifts.slice(currentIndex, currentIndex + 3).map((gift, i) => (
          <div
            key={gift.id}
            ref={i === 0 ? cardRef : undefined}
            className="absolute inset-0"
            style={getCardStyle(i)}
            {...(i === 0 && !flyOut
              ? {
                  onTouchStart,
                  onTouchMove,
                  onTouchEnd,
                  onMouseDown,
                }
              : {})}
          >
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden h-full flex flex-col select-none">
              {/* Image area */}
              <div
                className={`relative h-52 ${
                  gift.productImage
                    ? 'bg-gradient-to-br from-gray-50 to-gray-100'
                    : `bg-gradient-to-br ${gift.imageColor}`
                } flex items-center justify-center overflow-hidden flex-shrink-0`}
              >
                {gift.productImage ? (
                  <img
                    src={gift.productImage}
                    alt={gift.title}
                    className="h-full w-full object-contain p-6 mix-blend-multiply"
                    loading="eager"
                    draggable={false}
                  />
                ) : (
                  <span className="text-8xl select-none">{gift.imageEmoji}</span>
                )}

                {/* AI Confidence Badge */}
                {gift.aiConfidence && (
                  <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-xl shadow-lg">
                    <div className="flex items-center gap-1.5">
                      <Sparkles size={13} className="text-purple-600" />
                      <span className="text-xs font-bold text-purple-600">{gift.aiConfidence}% Match</span>
                    </div>
                  </div>
                )}

                {/* Swipe overlays */}
                {i === 0 && showLikeOverlay && (
                  <div
                    className="absolute inset-0 bg-green-500/10 flex items-center justify-center pointer-events-none"
                    style={{ opacity: swipeOverlayOpacity }}
                  >
                    <div
                      className="border-4 border-green-500 rounded-xl px-6 py-3 -rotate-12"
                      style={{ opacity: swipeOverlayOpacity }}
                    >
                      <span className="text-3xl font-black text-green-500 tracking-wider">BUY NOW</span>
                    </div>
                  </div>
                )}
                {i === 0 && showSkipOverlay && (
                  <div
                    className="absolute inset-0 bg-red-500/10 flex items-center justify-center pointer-events-none"
                    style={{ opacity: swipeOverlayOpacity }}
                  >
                    <div
                      className="border-4 border-red-500 rounded-xl px-6 py-3 rotate-12"
                      style={{ opacity: swipeOverlayOpacity }}
                    >
                      <span className="text-3xl font-black text-red-500 tracking-wider">SKIP</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Card content */}
              <div className="p-5 flex flex-col flex-1 overflow-hidden">
                {/* Badges */}
                {gift.badges && gift.badges.length > 0 && (
                  <div className="flex gap-2 mb-2">
                    {gift.badges.slice(0, 2).map((badge, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 text-xs font-bold rounded-full">
                        <Sparkles size={10} />
                        {badge}
                      </span>
                    ))}
                  </div>
                )}

                {/* Title + Price (System 1 info) */}
                <h3 className="text-lg font-bold mb-1 line-clamp-2">{gift.title}</h3>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl font-bold text-gray-900">
                    {getCurrencySymbol(gift.currency || 'USD')}{Math.round(gift.price)}
                  </span>
                  {gift.discountOriginalPrice && gift.discountOriginalPrice > gift.price && (
                    <span className="text-sm text-gray-400 line-through">
                      {getCurrencySymbol(gift.currency || 'USD')}{Math.round(gift.discountOriginalPrice)}
                    </span>
                  )}
                  {gift.trending && (
                    <span className="flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                      <Zap size={10} />
                      Trending
                    </span>
                  )}
                </div>

                {/* Why it's perfect — collapsed to 2 bullets, expandable */}
                {gift.aiReason && (
                  <div className="mb-2">
                    <ul className="space-y-1 text-sm text-gray-600">
                      {(gift.aiReason || '').split(/[.!,;]/).filter(s => s.trim().length > 10).slice(0, expanded ? 4 : 2).map((point, idx) => (
                        <li key={idx} className="flex items-start gap-1.5">
                          <Check size={13} className="text-purple-500 mt-0.5 flex-shrink-0" />
                          <span>{point.trim()}</span>
                        </li>
                      ))}
                    </ul>
                    {(gift.aiReason || '').split(/[.!,;]/).filter(s => s.trim().length > 10).length > 2 && (
                      <button
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpanded(!expanded);
                          if (!expanded) {
                            analytics.swipeCardExpanded(gift.title, currentIndex + 1);
                          }
                        }}
                        className="flex items-center gap-1 text-xs text-purple-500 font-medium mt-1 hover:text-purple-700"
                      >
                        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        {expanded ? 'Show less' : 'Why it\'s perfect'}
                      </button>
                    )}
                  </div>
                )}

                {/* Store + Rating (trust signals) */}
                <div className="mt-auto space-y-2">
                  <div className="flex items-center justify-between">
                    {gift.storeName && (
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <span className="text-xs">
                          {gift.storeName === 'Amazon' && '🛒'}
                          {gift.storeName === 'Etsy' && '🎨'}
                          {gift.storeName === 'KSP' && '🔌'}
                          {!['Amazon', 'Etsy', 'KSP'].includes(gift.storeName) && '🏪'}
                        </span>
                        <span className="font-medium">{gift.storeName}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(star => (
                        <Star
                          key={star}
                          size={13}
                          className={gift.rating && star <= Math.round(gift.rating)
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300'}
                        />
                      ))}
                      {gift.rating && (
                        <span className="text-xs text-gray-500 ml-1">
                          {gift.rating}{gift.reviewCount > 0 && ` (${gift.reviewCount.toLocaleString()})`}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Buy Now — preventDefault, onBuyClick handles app deep link */}
                  <a
                    href={gift.affiliateLink}
                    rel="noopener noreferrer"
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      animateOut('right');
                    }}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-3 px-5 rounded-2xl hover:shadow-xl hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
                  >
                    <span>Buy Now</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Action buttons + undo */}
      <div className="flex items-center justify-center gap-6 mt-5">
        {/* Skip button */}
        <button
          onClick={handleButtonSwipeLeft}
          disabled={!!flyOut}
          className="w-14 h-14 rounded-full bg-white shadow-lg border-2 border-red-200 flex items-center justify-center text-red-400 hover:bg-red-50 hover:border-red-300 hover:scale-110 transition-all disabled:opacity-50"
          aria-label="Skip this gift"
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Undo button */}
        {decisions.length > 0 && lastUndo && (
          <button
            onClick={handleUndo}
            className="w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-gray-50 hover:scale-110 transition-all"
            aria-label="Undo last swipe"
          >
            <RotateCcw size={16} />
          </button>
        )}

        {/* Like / Buy button */}
        <a
          href={currentGift?.affiliateLink || '#'}
          rel="noopener noreferrer"
          onClick={(e) => {
            e.preventDefault();
            if (flyOut) return;
            animateOut('right');
          }}
          className={`w-14 h-14 rounded-full bg-white shadow-lg border-2 border-green-200 flex items-center justify-center text-green-500 hover:bg-green-50 hover:border-green-300 hover:scale-110 transition-all ${flyOut ? 'opacity-50 pointer-events-none' : ''}`}
          aria-label="Buy this gift"
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </a>
      </div>

      {/* Swipe hint */}
      {currentIndex === 0 && decisions.length === 0 && (
        <p className="text-center text-xs text-gray-400 mt-3">
          Swipe right to buy &middot; Swipe left to skip
        </p>
      )}
    </div>
  );
}

/**
 * Mini card for the completion summary screen.
 */
function SummaryCard({ gift, liked, getCurrencySymbol, onClick }) {
  return (
    <a
      href={gift.affiliateLink}
      rel="noopener noreferrer"
      onClick={(e) => {
        e.preventDefault();
        if (onClick) onClick();
      }}
      className={`w-full flex items-center gap-4 p-3 rounded-2xl border-2 transition-all text-left no-underline ${
        liked
          ? 'border-green-200 bg-green-50/50 hover:border-green-300 hover:shadow-md'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
      }`}
    >
      {/* Thumbnail */}
      <div className={`w-16 h-16 rounded-xl flex-shrink-0 flex items-center justify-center overflow-hidden ${
        gift.productImage ? 'bg-gray-50' : `bg-gradient-to-br ${gift.imageColor}`
      }`}>
        {gift.productImage ? (
          <img src={gift.productImage} alt="" className="w-full h-full object-contain p-1" draggable={false} />
        ) : (
          <span className="text-3xl">{gift.imageEmoji}</span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <h4 className="font-bold text-sm truncate">{gift.title}</h4>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="font-bold text-gray-900">
            {getCurrencySymbol(gift.currency || 'USD')}{Math.round(gift.price)}
          </span>
          {gift.storeName && (
            <span className="text-xs text-gray-500">on {gift.storeName}</span>
          )}
        </div>
      </div>

      {/* CTA */}
      <div className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold ${
        liked
          ? 'bg-green-500 text-white'
          : 'bg-orange-500 text-white'
      }`}>
        Buy Now
      </div>
    </a>
  );
}
